import React, { useEffect, useRef, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import "../../components/modals/modal.css";
import YuJaButton from "../../components/standardization/YuJaButton";
import { YuJaDropdown } from "../../components/standardization/YuJaDropdown";
import { YuJaTextBox } from "../../components/standardization/YuJaTextBox";
import { ReactComponent as ModalClose } from "../../images/modal_close.svg";
import { BUTTON, CREATION_ROLES, REGEX_EMAIL, TEXTBOX, USER_CREATION_FIELDS } from "../../utils/constants";
import { useTranslation } from "react-i18next";

const characterRegexp = /^[\w\s]+$/g;
const phoneRegexp = /^(\d{3}-\d{3}-\d{4}|\d{10})$/g;
const testCap = new RegExp("^(?=.*[a-z])(?=.*[A-Z]).+$");
const testSpe = new RegExp("^(?=.*[-+_!@#$%^&*.,?]).+$");


export default React.memo(UserModal, (prevProps, nextProps) => {
    if (prevProps.isShow !== nextProps.isShow) {
        return false;
    }
    return prevProps.isNew === nextProps.isNew;
})

function UserModal({ checkExisted, submit, close, user = {}, isNew = true, isShow = false }) {
    const { t } = useTranslation();
    const characterRequires = t("user.invalidCharactersError");
    const emailRequires = t("user.invalidEmailFormatError");
    const phoneRequires = t("user.invalidPhoneFormatError");
    const [userState, setUserState] = useState({
        userName: "",
        password: "",
        passwordConfirm: "",
        role: "",
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
        phone: ""
    });
    const userNameRef = useRef();
    const passwordRef = useRef();
    const roleRef = useRef();
    const passwordConfirmRef = useRef();
    const firstNameRef = useRef();
    const middleNameRef = useRef();
    const lastNameRef = useRef();
    const emailRef = useRef();
    const phoneRef = useRef();
    const [loadingState, setLoadingState] = useState(false);
    const [invalidDict, setInvalidDict] = useState({});

    const handleSave = async () => {
        let validateRes = validateData();
        if (!validateRes) {
            return;
        }

        let existedRes;
        setLoadingState(true);
        await checkUserNameExisted(userState.userName).then((res) => {
            existedRes = res;
        }).catch(() => {
            existedRes = true;
        });

        if (existedRes) {
            setLoadingState(false);
            return;
        }

        submit(userState, isNew).finally(() => {
            setLoadingState(false);
        });

    };

    const handleClose = () => {
        close();
    };

    const checkUserNameExisted = (userName) => {
        // setLoadingState(true);
        return new Promise((resolve, reject) => {
            if (userName === user.userName) {
                resolve(false);
                return;
            }

            checkExisted({ userName: userName }).then(res => {
                resolve(res);
                if (res) {
                    let ele = document.getElementsByName("userName")[0];
                    if(ele !== null) {
                        const invalidJsonCopy = JSON.parse(JSON.stringify(invalidDict));
                        invalidJsonCopy[ele.name] = t("user.usernameExistsError");
                        setInvalidDict(invalidJsonCopy);
                    }
                }
            }).catch(() => {
                reject();
            });
        })
    }

    const validateData = () => {
        let validateRes = true;
        const invalidJsonCopy = JSON.parse(JSON.stringify(invalidDict));
        Object.entries(userState).filter(([key]) => {
            return document.getElementsByName(key).length > 0;
        }).forEach(([key]) => {
            const ele = document.getElementsByName(key)[0];
            const res = validateInputNew(ele, invalidJsonCopy);
            if (!res) {
                validateRes = res;
            }
        });

        setInvalidDict(invalidJsonCopy);
        return validateRes;
    };

    useEffect(() => {
        const userObj = Object.assign({
            userName: "",
            password: "",
            passwordConfirm: "",
            role: "",
            firstName: "",
            middleName: "",
            lastName: "",
            email: "",
            phone: "",
            instId: ""
        }, user);
        setUserState(userObj);
        setInvalidDict({});
    }, [user, isShow, isNew]);

    const changeVal = (event) => {
        const input = event.currentTarget;
        const user = Object.assign({}, userState);
        user[input.name] = input.value;
        setUserState(user);
    }

    const validateInputNew = (input, invalidJsonCopy) => {
        let ele;
        if (input.currentTarget) {
            ele = input.currentTarget;
            if (input.preventDefault) {
                input.preventDefault();
            }
            if (input.stopPropagation) {
                input.stopPropagation();
            }
        } else {
            ele = input;
        }

        if (!invalidJsonCopy) {
            invalidJsonCopy = JSON.parse(JSON.stringify(invalidDict));
        }

        if (ele.name !== "middleName" && ele.name !== "phone" && !validateBlankNew(ele, invalidJsonCopy)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        }

        if (ele.name === "userName" || ele.name === "firstName" || ele.name === "lastName") {
            if (!validateCharacterNew(ele, invalidJsonCopy)) {
                setInvalidDict(invalidJsonCopy);
                return false;
            }
        }

        if (ele.name === "middleName" && ele.value && !validateCharacterNew(ele, invalidJsonCopy)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        }

        if (ele.name === "password") {
            if (validatePasswordNew(ele, invalidJsonCopy)) {
                delete invalidJsonCopy[ele.name];
                if (checkPasswordEqualNew(invalidJsonCopy)) {
                    delete invalidJsonCopy["passwordConfirm"];
                    setInvalidDict(invalidJsonCopy);
                } else {
                    setInvalidDict(invalidJsonCopy);
                    return false;
                }
            } else {
                invalidJsonCopy[ele.name] = t("auth.invalidPasswordRequirements") + invalidJsonCopy[ele.name];
                setInvalidDict(invalidJsonCopy);
                return false;
            }
        }

        if (ele.name === "passwordConfirm") {
            if (checkPasswordEqualNew(invalidJsonCopy)) {
                delete invalidJsonCopy[ele.name];
                setInvalidDict(invalidJsonCopy);
            } else {
                setInvalidDict(invalidJsonCopy);
                return false;
            }
        }

        if (ele.name === "email" && !validateEmailNew(ele,invalidJsonCopy)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        }

        if (ele.name === USER_CREATION_FIELDS.USERNAME && !validateCharacterLimit(ele, invalidJsonCopy, 100)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        }
        if ((ele.name === USER_CREATION_FIELDS.FIRST_NAME || ele.name === USER_CREATION_FIELDS.MIDDLE_NAME || ele.name === USER_CREATION_FIELDS.LAST_NAME) && !validateCharacterLimit(ele, invalidJsonCopy, 50)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        }

        if (ele.name === "phone" && ele.value.length > 0 && !validatePhoneNew(ele,invalidJsonCopy)) {
            setInvalidDict(invalidJsonCopy);
            return false;
        } else {
            delete invalidJsonCopy[ele.name];
        }

        setInvalidDict(invalidJsonCopy);
        return true;
    }

    const validateInput = (input) => {
        let ele;
        if (input.currentTarget) {
            ele = input.currentTarget;
            input.preventDefault();
            input.stopPropagation();
        } else {
            ele = input;
        }

        if (ele !== middleNameRef.current && ele !== phoneRef.current && !validateBlank(ele)) {
            return false;
        }

        if (ele === userNameRef.current || ele === firstNameRef.current || ele === lastNameRef.current) {
            if (!validateCharacter(ele)) {
                return false;
            }
        }

        if (ele === middleNameRef.current && ele.value && !validateCharacter(ele)) {
            return false;
        }

        if (ele === passwordRef.current || ele === passwordConfirmRef.current) {
            if(validatePassword(ele) && checkPasswordEqual(passwordRef.current)) {
                passwordRef.current.classList.remove("is-invalid");
                passwordConfirmRef.current.classList.remove("is-invalid");
                return true;
            } 
            return false;
        }
        
        if (ele === emailRef.current && !validateEmail(ele)) {
            return false;
        }

        if (ele === phoneRef.current && ele.value.length > 0 && !validatePhone(ele)) {
            return false;
        }

        ele.classList.remove("is-invalid");
        return true;

    };

    const validateCharacterLimit = (ele, copy, limit) => {
        if (ele.value.length > limit) {
            copy[ele.name] = t("common.characterLimitExceeded").replace("length", limit);
            return false;
        }
        return true; 
    }

    const validateBlank = (ele) => {
        if (!ele.value) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = ele === roleRef.current ? ele.nextSibling.innerHTML : t("user.fillFieldError", {field: ele.placeholder});
            return false;
        }
        return true;
    };

    const validateBlankNew = (ele, copy) => {
        if (ele.name === "role" && !ele.value) {
            copy[ele.name] = t("user.selectRoleError");
            return false;
        }


        if (!ele.value) {
            copy[ele.name] = t("user.fillFieldError", {field: ele.placeholder});
            return false;
        }

        if (copy.hasOwnProperty(ele.name)) {
            delete copy[ele.name];
        }
        return true;
    }

    const validateCharacter = (ele) => {
        if (ele.value.search(characterRegexp) < 0) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = characterRequires;
            return false;
        }
        return true;
    };

    const validateCharacterNew = (ele, copy) => {
        if (ele.value.search(characterRegexp) < 0) {
            copy[ele.name] = characterRequires;
            return false;
        }

        if (copy.hasOwnProperty(ele.name)) {
            delete copy[ele.name];
        }
        return true;
    };

    const checkPasswordEqual = (ele) => {
        if (passwordConfirmRef.current.value !== ele.value) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = t("auth.passwordNotMatch");
            return false;
        }
        return true;
    };

    const checkPasswordEqualNew = (copy) => {
        if (userState.password !== userState.passwordConfirm) {
            copy["passwordConfirm"] = t("auth.passwordNotMatch");
            return false;
        }
        return true;
    };

    const validatePassword = (ele) => {
        ele.nextSibling.innerHTML = "";
        if (ele.value.length < 12 || ele.value.length > 30) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = "\t" + t("auth.passwordRequirementLength");
        }
        if (!testCap.test(ele.value)) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML += ele.nextSibling.innerHTML ? "\n\t" : "\t" + t("auth.passwordRequirementCapital");
        }
        if (!/\d/.test(ele.value)) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML += ele.nextSibling.innerHTML ? "\n\t" : "\t" + t("auth.passwordRequirementNumber");
        }
        if (!testSpe.test(ele.value)) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML += ele.nextSibling.innerHTML ? "\n\t" : "\t" + t("auth.passwordRequirementSpecial");
        }
        return !ele.nextSibling.innerHTML;
    };

    const validatePasswordNew = (ele, copy) => {
        copy[ele.name] = "";
        if (ele.value.length < 12 || ele.value.length > 30) {
            copy[ele.name] = "\t" + t("auth.passwordRequirementLength");
        }
        if (!testCap.test(ele.value)) {
            copy[ele.name] += (copy[ele.name] ? "\n\t" : "\t") + t("auth.passwordRequirementCapital");
        }
        if (!/\d/.test(ele.value)) {
            copy[ele.name] += (copy[ele.name] ? "\n\t" : "\t") + t("auth.passwordRequirementNumber");
        }
        if (!testSpe.test(ele.value)) {
            copy[ele.name] += (copy[ele.name] ? "\n\t" : "\t") + t("auth.passwordRequirementSpecial");
        }
        return !copy[ele.name];
    };

    const validateEmail = (ele) => {
        if (ele.value.search(REGEX_EMAIL) < 0) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = emailRequires;
            return false;
        }
        return true;
    };

    const validateEmailNew = (ele, copy) => {
        if (ele.value.search(REGEX_EMAIL) < 0) {
            copy[ele.name] = emailRequires;
            return false;
        }

        if (copy.hasOwnProperty(ele.name)) {
            delete copy[ele.name];
        }
        return true;
    };

    const validatePhone = (ele) => {
        if (ele.value.search(phoneRegexp) < 0) {
            ele.classList.add("is-invalid");
            ele.nextSibling.innerHTML = phoneRequires;
            return false;
        }
        return true;
    };

    const validatePhoneNew = (ele, copy) => {
        if (ele.value.search(phoneRegexp) < 0) {
            copy[ele.name] = phoneRequires;
            return false;
        }

        if (copy.hasOwnProperty(ele.name)) {
            delete copy[ele.name];
        }
        return true;
    };

    return (
        <Modal
            id="form-modal"
            show={isShow}
            onHide={handleClose}
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={true}
        >
            <ModalClose tabIndex={0} aria-label={t("user.cancelCreateUser")} role={BUTTON} onClick={handleClose} className="modal-close" />
            <Modal.Header
                style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Modal.Title
                    className="modal-title"
                    id="contained-modal-title-vcenter"
                    tabIndex={0} 
                    aria-label={(isNew ? t("user.createUser") : t("user.updateUser")) + t("accessibility.modalSuffix")}
                    role={TEXTBOX}
                >
                    {isNew ? t("user.createUser") : t("user.updateUser")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group controlId="username">
                        <div className={"form-label"}>{t("auth.usernameLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("auth.usernameLabel")}
                            name="userName"
                            placeholder={t("auth.usernameLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.userName}
                            isInvalid={!!invalidDict["userName"]}
                            errorMsg={invalidDict["userName"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                        <Form.Control.Feedback type="invalid">{t("auth.usernameSubtitle")}</Form.Control.Feedback>
                    </Form.Group>

                    {!isNew ? (<></>) : (
                        <>
                            <Form.Group controlId="password">
                                <div className={"form-label"}>{t("auth.passwordLabel")}</div>
                                <YuJaTextBox
                                    label={t("accessibility.textboxEnterPrefix") + t("auth.passwordLabel")}
                                    name="password"
                                    type="password"
                                    placeholder={t("auth.passwordLabel")}
                                    onBlur={validateInputNew}
                                    onChange={changeVal}
                                    value={userState.password}
                                    isInvalid={!!invalidDict["password"]}
                                    errorMsg={invalidDict["password"]}
                                    containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                                />
                            </Form.Group>

                            <Form.Group controlId="password confirm">
                                <div className={"form-label"}>{t("auth.confirmPasswordLabel")}</div>
                                <YuJaTextBox
                                    label={t("accessibility.textboxEnterPrefix") + t("auth.confirmPasswordLabel")}
                                    name="passwordConfirm"
                                    type="password"
                                    placeholder={t("auth.confirmPasswordLabel")}
                                    onBlur={validateInputNew}
                                    onChange={changeVal}
                                    value={isNew ? userState.passwordConfirm : userState.password}
                                    isInvalid={!!invalidDict["passwordConfirm"]}
                                    errorMsg={invalidDict["passwordConfirm"]}
                                    containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                                />
                            </Form.Group>
                        </>
                    )}

                    <Form.Group controlId="role">
                        <div className={"form-label"}>{t("auth.roleLabel")}</div>
                        <YuJaDropdown
                            label={t("user.userRoleDropdown")}
                            placeholder={t("integration.selectRole")}
                            name="role"
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                            style={{padding: 6}}
                            data={Object.values(CREATION_ROLES)}
                            getOptionLabel={(item) => item.display}
                            value={userState.role}
                            isInvalid={!!invalidDict["role"]}
                            errorMsg={invalidDict["role"]}
                            onChange={item => {
                                const e = {currentTarget: {value: item.value, name: "role"}};
                                changeVal(e);
                                validateInputNew(e);
                            }}
                        />
                        {/*<Form.Select name="role"*/}
                        {/*    ref={roleRef}*/}
                        {/*    value={userState.role}*/}
                        {/*    onChange={e => {*/}
                        {/*        changeVal(e);*/}
                        {/*        validateInput(e);*/}
                        {/*    }}*/}
                        {/*     style={{width: "95%", fontSize: 13}}*/}
                        {/*>*/}
                        {/*    <option value="">select...</option>*/}
                        {/*    {Object.keys(CREATION_ROLES).map((key, index) => { return <option value={CREATION_ROLES[key].value} key={index}>{CREATION_ROLES[key].display}</option> })}*/}
                        {/*</Form.Select>*/}
                        <Form.Control.Feedback type="invalid">{t("auth.roleSubtitle")}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group controlId="first name">
                        <div className={"form-label"}>{t("auth.firstNameLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("auth.firstNameLabel")}
                            name="firstName"
                            placeholder={t("auth.firstNameLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.firstName}
                            isInvalid={!!invalidDict["firstName"]}
                            errorMsg={invalidDict["firstName"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                    </Form.Group>

                    <Form.Group controlId="middle name">
                        <div className={"form-label"}>{t("auth.middleNameLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("auth.middleNameLabel")}
                            name="middleName"
                            placeholder={t("auth.middleNameLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.middleName}
                            isInvalid={!!invalidDict["middleName"]}
                            errorMsg={invalidDict["middleName"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                    </Form.Group>

                    <Form.Group controlId="last name">
                        <div className={"form-label"}>{t("auth.lastNameLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("auth.lastNameLabel")}
                            name="lastName"
                            placeholder={t("auth.lastNameLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.lastName}
                            isInvalid={!!invalidDict["lastName"]}
                            errorMsg={invalidDict["lastName"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                    </Form.Group>

                    <Form.Group controlId="email">
                        <div className={"form-label"}>{t("common.emailLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("common.emailLabel")}
                            name="email"
                            placeholder={t("common.emailLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.email}
                            isInvalid={!!invalidDict["email"]}
                            errorMsg={invalidDict["email"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                    </Form.Group>

                    <Form.Group controlId="phone">
                        <div className={"form-label"}>{t("auth.phoneLabel")}</div>
                        <YuJaTextBox
                            label={t("accessibility.textboxEnterPrefix") + t("auth.phoneLabel")}
                            name="phone"
                            placeholder={t("auth.phoneLabel")}
                            onBlur={validateInputNew}
                            onChange={changeVal}
                            value={userState.phone}
                            isInvalid={!!invalidDict["phone"]}
                            errorMsg={invalidDict["phone"]}
                            containerStyle={{width: "95%", fontSize: 13, lineHeight: 1.6,}}
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <YuJaButton aria-label={t("user.cancelCreateUser")} type={"secondary"} onClick={handleClose}>
                    {t("common.cancel")}
                </YuJaButton>
                {loadingState ? (
                    <YuJaButton disabled={true}  onClick={handleSave} style={{minWidth: 60}}>
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                        />
                    </YuJaButton>
                ) : (
                    <YuJaButton onClick={handleSave} style={{minWidth: 60}}>
                        {t("common.save")}
                    </YuJaButton>
                )}
            </Modal.Footer>
        </Modal>

    );
}
